import {Component, computed, inject, input, signal} from '@angular/core';
import {DatePipe} from '@angular/common';
import {rxResource} from '@angular/core/rxjs-interop';
import {firstValueFrom} from 'rxjs';
import {TranslocoPipe} from '@jsverse/transloco';
import {Button} from 'primeng/button';
import {Message} from 'primeng/message';
import {Tag} from 'primeng/tag';
import {
  DownloadProviderDocumentRequestParams,
  GetTemplateScoreHistoryRequestParams,
  ProviderDocumentControllerService,
  TemplateScoreHistoryControllerService,
} from '@frontend/shared-api';
import {ToastService} from '@frontend/toast';
import {SubcriteriaAttribute} from '../../provider-questionnaire.types';
import {FileDownloadService} from '../../../../utils/file-download.service';
import {DocumentPreviewDialogService} from '../../../dialog/document-preview-dialog/document-preview-dialog.service';
import {ModelFieldTextComponent} from '../../../inputs/model-field/model-field-text/model-field-text.component';
import {AttributeFulfillmentInputComponent} from '../../fulfillment-input/attribute-fulfillment-input.component';
import {AttributeFulfillmentChange} from '../../fulfillment-input/attribute-fulfillment-input.types';
import {SuggestionAnswerDisplayComponent} from '../suggestion-answer-display/suggestion-answer-display.component';
import {describeAnswer} from '../suggestion-answer-display/answer-display.utils';
import {TemplateSuggestionStore} from '../template-suggestion.store';
import {
  CONFIDENCE_SEVERITY,
  EVIDENCE_GRADE_SEVERITY,
  OverrideValue,
  SUGGESTION_KEY_PREFIX,
  SuggestionTagSeverity,
} from '../template-suggestion.types';
import {EvidenceRow} from './template-suggestion-review.types';

@Component({
  selector: 'app-template-suggestion-review',
  imports: [
    TranslocoPipe,
    DatePipe,
    Button,
    Message,
    Tag,
    ModelFieldTextComponent,
    AttributeFulfillmentInputComponent,
    SuggestionAnswerDisplayComponent,
  ],
  templateUrl: './template-suggestion-review.component.html',
})
export class TemplateSuggestionReviewComponent {
  private readonly store = inject(TemplateSuggestionStore);
  private readonly historyService = inject(TemplateScoreHistoryControllerService);
  private readonly documentService = inject(ProviderDocumentControllerService);
  private readonly previewDialog = inject(DocumentPreviewDialogService);
  private readonly fileDownload = inject(FileDownloadService);
  private readonly toast = inject(ToastService);

  readonly attribute = input.required<SubcriteriaAttribute>();
  readonly twoStage = input(false);
  readonly expanded = input(false);
  readonly disabled = input(false);

  protected readonly overriding = signal(false);
  protected readonly reason = signal<string | null>(null);
  protected readonly markingClarification = signal(false);
  protected readonly clarificationNote = signal<string | null>(null);
  private readonly draft = signal<AttributeFulfillmentChange | null>(null);
  private readonly historyVersion = signal(0);

  private readonly historyResource = rxResource({
    params: () => this.historyRequest(),
    stream: ({params: request}) => this.historyService.getTemplateScoreHistory(request),
  });

  protected readonly showsReview = computed(() => this.store.enabled());
  protected readonly openSuggestion = computed(() =>
    this.store.openSuggestionFor(this.attribute().directusId),
  );
  private readonly reviewRequiredSuggestion = computed(() =>
    this.store.reviewRequiredSuggestionFor(this.attribute().directusId),
  );
  protected readonly suggestion = computed(
    () => this.openSuggestion() ?? this.reviewRequiredSuggestion() ?? null,
  );
  protected readonly clarification = computed(() =>
    this.store.clarificationFor(this.attribute().directusId),
  );
  protected readonly busy = computed(() => {
    const suggestion = this.suggestion();
    return suggestion !== null && this.store.isBusy(suggestion.id);
  });
  protected readonly clarificationBusy = computed(() =>
    this.store.isClarificationBusy(this.attribute().directusId),
  );
  private readonly showsActions = computed(
    () => this.openSuggestion() !== undefined && !this.disabled(),
  );
  protected readonly showsPrimaryActions = computed(
    () => this.showsActions() && !this.overriding(),
  );
  protected readonly showsOverrideEditor = computed(() => this.showsActions() && this.overriding());
  private readonly canEditClarification = computed(
    () => this.clarification() === undefined && !this.disabled(),
  );
  protected readonly showsMarkClarification = computed(
    () => this.canEditClarification() && !this.markingClarification(),
  );
  protected readonly showsClarificationEditor = computed(
    () => this.canEditClarification() && this.markingClarification(),
  );
  protected readonly showsClearClarification = computed(
    () => this.clarification() !== undefined && !this.disabled(),
  );
  protected readonly confidenceKey = computed(() => {
    const suggestion = this.suggestion();
    return suggestion ? `${SUGGESTION_KEY_PREFIX}.confidence.${suggestion.confidence}` : null;
  });
  protected readonly confidenceSeverity = computed<SuggestionTagSeverity>(() => {
    const suggestion = this.suggestion();
    return suggestion ? CONFIDENCE_SEVERITY[suggestion.confidence] : 'info';
  });
  protected readonly suggestedAnswer = computed(() => {
    const suggestion = this.suggestion();
    if (!suggestion) return null;
    return describeAnswer(this.twoStage(), this.attribute().fulfillmentType, {
      score: suggestion.suggestedScore,
      scoreAbsent: false,
      capabilityLevel: suggestion.capabilityLevel,
      provisioningType: suggestion.provisioningType,
    });
  });
  protected readonly currentAnswer = computed(() =>
    describeAnswer(this.twoStage(), this.attribute().fulfillmentType, this.attribute()),
  );
  protected readonly evidence = computed<EvidenceRow[]>(() =>
    (this.suggestion()?.evidence ?? []).map((evidence) => ({
      evidence,
      locationKey: `${SUGGESTION_KEY_PREFIX}.review.evidence.location.${evidence.locationType}`,
      gradeKey: `${SUGGESTION_KEY_PREFIX}.review.evidence.grade.${evidence.grade}`,
      gradeSeverity: EVIDENCE_GRADE_SEVERITY[evidence.grade],
      openable: evidence.documentId !== undefined && evidence.documentId !== null,
      previewable: evidence.fileName.toLowerCase().endsWith('.pdf'),
    })),
  );
  protected readonly hasEvidence = computed(() => this.evidence().length > 0);
  protected readonly reviewRequiredVersion = computed(() => {
    const suggestion = this.reviewRequiredSuggestion();
    if (this.openSuggestion() !== undefined || !suggestion) return null;
    return suggestion.productVersion ?? null;
  });
  protected readonly latestChange = computed(
    () => this.historyResource.value()?.entries[0] ?? null,
  );
  protected readonly latestChangeSourceKey = computed(() => {
    const change = this.latestChange();
    return change ? `${SUGGESTION_KEY_PREFIX}.review.source.${change.source}` : null;
  });
  protected readonly historyLoading = computed(() => this.historyResource.isLoading());
  protected readonly draftAttribute = computed<SubcriteriaAttribute>(() => {
    const suggestion = this.suggestion();
    const attribute = this.attribute();
    if (!suggestion) return attribute;
    return {
      ...attribute,
      score: suggestion.suggestedScore,
      scoreAbsent: false,
      capabilityLevel: suggestion.capabilityLevel,
      provisioningType: suggestion.provisioningType,
      singleStage: false,
      hasChanges: false,
    };
  });
  protected readonly canConfirmOverride = computed(() => {
    const draft = this.draft();
    const suggestion = this.openSuggestion();
    if (!draft || !suggestion || draft.scoreAbsent || draft.score === undefined) return false;
    if (this.twoStage()) {
      return (
        draft.capabilityLevel !== undefined &&
        draft.provisioningType !== undefined &&
        (draft.capabilityLevel !== suggestion.capabilityLevel ||
          draft.provisioningType !== suggestion.provisioningType)
      );
    }
    return draft.score !== suggestion.suggestedScore;
  });

  protected async accept(): Promise<void> {
    const suggestion = this.openSuggestion();
    if (!suggestion) return;
    await this.store.accept(suggestion);
    this.historyVersion.update((version) => version + 1);
    await this.toast.showSuccess(`${SUGGESTION_KEY_PREFIX}.toast.accepted`);
  }

  protected async reject(): Promise<void> {
    const suggestion = this.openSuggestion();
    if (!suggestion) return;
    await this.store.reject(suggestion);
    await this.toast.showSuccess(`${SUGGESTION_KEY_PREFIX}.toast.rejected`);
  }

  protected startOverride(): void {
    this.draft.set(null);
    this.reason.set(null);
    this.overriding.set(true);
  }

  protected cancelOverride(): void {
    this.overriding.set(false);
  }

  protected onDraftChange(change: AttributeFulfillmentChange): void {
    this.draft.set(change);
  }

  protected async confirmOverride(): Promise<void> {
    const suggestion = this.openSuggestion();
    const draft = this.draft();
    if (!suggestion || !draft || draft.score === undefined) return;
    const value: OverrideValue = {
      score: draft.score,
      capabilityLevel: draft.capabilityLevel,
      provisioningType: draft.provisioningType,
      comment: this.attribute().comment,
      reason: this.reason() ?? undefined,
    };
    await this.store.override(suggestion, value);
    this.overriding.set(false);
    this.historyVersion.update((version) => version + 1);
    await this.toast.showSuccess(`${SUGGESTION_KEY_PREFIX}.toast.overridden`);
  }

  protected startClarification(): void {
    this.clarificationNote.set(null);
    this.markingClarification.set(true);
  }

  protected cancelClarification(): void {
    this.markingClarification.set(false);
  }

  protected async confirmClarification(): Promise<void> {
    await this.store.markClarification(this.attribute().directusId, this.clarificationNote());
    this.markingClarification.set(false);
    await this.toast.showSuccess(`${SUGGESTION_KEY_PREFIX}.clarification.toast.marked`);
  }

  protected async clearClarification(): Promise<void> {
    await this.store.clearClarification(this.attribute().directusId);
    await this.toast.showSuccess(`${SUGGESTION_KEY_PREFIX}.clarification.toast.cleared`);
  }

  protected async openEvidence(row: EvidenceRow): Promise<void> {
    const documentId = row.evidence.documentId;
    if (documentId === undefined || documentId === null) return;
    const request: DownloadProviderDocumentRequestParams = {documentId};
    if (row.previewable) {
      const blob = await firstValueFrom(this.documentService.downloadProviderDocument(request));
      this.previewDialog.open(blob);
      return;
    }
    const response = await firstValueFrom(
      this.documentService.downloadProviderDocument(request, 'response'),
    );
    this.fileDownload.downloadFromResponse(response);
  }

  private historyRequest(): GetTemplateScoreHistoryRequestParams | undefined {
    const context = this.store.context();
    this.historyVersion();
    if (!this.expanded() || !context) return undefined;
    return {
      toolId: context.toolId,
      attributeDirectusId: this.attribute().directusId,
      phase: context.phase,
    };
  }
}
